import React, { useEffect, useRef } from 'react';

// 神经网络节点
interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  pulsePhase: number;
}

// 连线
interface Link {
  from: number;
  to: number;
  strength: number;
}

// 数据流粒子
interface Particle {
  linkIndex: number;
  progress: number;
  speed: number;
  color: string;
}

const ModelMarket: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const nodesRef = useRef<Node[]>([]);
  const linksRef = useRef<Link[]>([]);
  const particlesRef = useRef<Particle[]>([]);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // 鼠标跟踪
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handleMouseMove);

    // 配色方案
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#06b6d4', '#10b981', '#f59e0b'];

    // 初始化节点
    const initNodes = () => {
      const nodeCount = Math.floor((canvas.width * canvas.height) / 20000);
      nodesRef.current = Array.from({ length: Math.max(25, Math.min(nodeCount, 60)) }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 1.5,
        vy: (Math.random() - 0.5) * 1.5,
        radius: Math.random() * 3 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        pulsePhase: Math.random() * Math.PI * 2,
      }));
    };
    initNodes();

    // 更新连线
    const updateLinks = () => {
      const nodes = nodesRef.current;
      const maxDistance = 180;
      const links: Link[] = [];

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < maxDistance) {
            links.push({
              from: i,
              to: j,
              strength: 1 - dist / maxDistance,
            });
          }
        }
      }
      linksRef.current = links;
    };

    let time = 0;

    // 动画循环
    const animate = () => {
      time += 0.016;

      // 清空画布 - 使用极深色背景
      ctx.fillStyle = '#050508';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 更新节点位置
      nodesRef.current.forEach((node) => {
        // 基础游动
        node.x += node.vx;
        node.y += node.vy;

        // 添加正弦波游动效果
        node.x += Math.sin(time * 0.5 + node.pulsePhase) * 0.3;
        node.y += Math.cos(time * 0.3 + node.pulsePhase) * 0.3;

        // 鼠标交互 - 节点会被鼠标吸引
        const dx = mouseRef.current.x - node.x;
        const dy = mouseRef.current.y - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200 && dist > 0) {
          node.vx += (dx / dist) * 0.02;
          node.vy += (dy / dist) * 0.02;
        }

        // 速度限制
        const speed = Math.sqrt(node.vx * node.vx + node.vy * node.vy);
        if (speed > 2) {
          node.vx = (node.vx / speed) * 2;
          node.vy = (node.vy / speed) * 2;
        }

        // 边界反弹
        if (node.x < 50) node.vx += 0.05;
        if (node.x > canvas.width - 50) node.vx -= 0.05;
        if (node.y < 50) node.vy += 0.05;
        if (node.y > canvas.height - 50) node.vy -= 0.05;

        // 保持在边界内
        node.x = Math.max(20, Math.min(canvas.width - 20, node.x));
        node.y = Math.max(20, Math.min(canvas.height - 20, node.y));
      });

      // 更新连线
      updateLinks();

      // 绘制连线 - 带流动效果
      linksRef.current.forEach((link, i) => {
        const from = nodesRef.current[link.from];
        const to = nodesRef.current[link.to];

        // 流动效果 - 线条透明度随时间变化
        const flow = Math.sin(time * 2 + i * 0.1) * 0.3 + 0.7;
        const alpha = link.strength * 0.4 * flow;

        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.strokeStyle = `rgba(99, 102, 241, ${alpha})`;
        ctx.lineWidth = link.strength * 2;
        ctx.stroke();

        // 连线的光晕效果
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.strokeStyle = `rgba(139, 92, 246, ${alpha * 0.3})`;
        ctx.lineWidth = link.strength * 6;
        ctx.stroke();
      });

      // 生成新的数据流粒子
      if (Math.random() < 0.15 && linksRef.current.length > 0) {
        const randomLink = Math.floor(Math.random() * linksRef.current.length);
        particlesRef.current.push({
          linkIndex: randomLink,
          progress: 0,
          speed: 0.015 + Math.random() * 0.025,
          color: colors[Math.floor(Math.random() * colors.length)],
        });
      }

      // 更新和绘制数据流粒子
      particlesRef.current = particlesRef.current.filter(particle => {
        particle.progress += particle.speed;

        if (particle.progress >= 1) {
          return false;
        }

        const link = linksRef.current[particle.linkIndex];
        if (!link) return false;

        const from = nodesRef.current[link.from];
        const to = nodesRef.current[link.to];

        const x = from.x + (to.x - from.x) * particle.progress;
        const y = from.y + (to.y - from.y) * particle.progress;

        // 粒子轨迹拖尾
        const prevX = from.x + (to.x - from.x) * (particle.progress - 0.05);
        const prevY = from.y + (to.y - from.y) * (particle.progress - 0.05);

        ctx.beginPath();
        ctx.moveTo(prevX, prevY);
        ctx.lineTo(x, y);
        ctx.strokeStyle = particle.color + '60';
        ctx.lineWidth = 2;
        ctx.stroke();

        // 绘制粒子核心
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();

        // 粒子光晕
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, 12);
        gradient.addColorStop(0, particle.color + '80');
        gradient.addColorStop(0.5, particle.color + '40');
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, 12, 0, Math.PI * 2);
        ctx.fill();

        return true;
      });

      // 绘制节点
      nodesRef.current.forEach(node => {
        const pulse = Math.sin(time * 3 + node.pulsePhase) * 0.3 + 1;

        // 节点外发光 - 脉动效果
        const gradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius * 6 * pulse
        );
        gradient.addColorStop(0, node.color + '60');
        gradient.addColorStop(0.3, node.color + '30');
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 6 * pulse, 0, Math.PI * 2);
        ctx.fill();

        // 节点核心
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * pulse, 0, Math.PI * 2);
        ctx.fillStyle = node.color;
        ctx.fill();

        // 节点高光
        ctx.beginPath();
        ctx.arc(node.x - node.radius * 0.3, node.y - node.radius * 0.3, node.radius * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050508] relative overflow-hidden">
      {/* 返回按钮 */}
      <a
        href="/"
        className="fixed top-4 left-4 z-50 flex items-center gap-2 px-4 py-2 text-slate-500 hover:text-slate-300 transition-colors bg-slate-800/20 rounded-lg backdrop-blur-sm text-sm"
      >
        ←
      </a>

      {/* Canvas 动画 */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
      />
    </div>
  );
};

export default ModelMarket;
